import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { useCallback, useRef } from 'react';
import { Button, Col, FormLabel, FormGroup, Row } from 'react-bootstrap';

import { uploadPatientActivityFiles } from 'App/components/PatientProfile/PatientProfile.utils.js';
import SelectField from 'Shared/form/SelectField';

const propTypes = {
    uploadLimit: PropTypes.string.isRequired,
    uploadLimitInt: PropTypes.string.isRequired,
    notesCount: PropTypes.number.isRequired,
    notes: PropTypes.arrayOf(
        PropTypes.shape({
            parentNoteId: PropTypes.string.isRequired,
            subject: PropTypes.string.isRequired,
        })
    ).isRequired,
    patientId: PropTypes.string.isRequired,
    onHide: PropTypes.func.isRequired,
};
const defaultProps = {};

const FileUploadForm = ({ uploadLimit, uploadLimitInt, notesCount, notes, patientId, onHide }) => {
    const fileInputRef = useRef();
    const initialValues = {
        activity: notes[0] || '',
        description: '',
        file: null,
        showRequiredError: false,
        showMaxSizeError: false,
        errorFileSize: 0,
    };

    const validateFile = useCallback((file, setFieldValue) => {
        if (file) {
            setFieldValue('showRequiredError', false);
            if (file.size <= uploadLimitInt) {
                setFieldValue('showMaxSizeError', false);
                return true;
            }
            setFieldValue('showMaxSizeError', true);
            setFieldValue('errorFileSize', file.size);
            fileInputRef.current.value = '';
        } else {
            setFieldValue('showRequiredError', true);
            fileInputRef.current.value = '';
        }
        return false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Row>
            <Col md={12}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={(values, { setSubmitting, setFieldValue }) => {
                        setFieldValue('errorMsg', null);
                        if (!validateFile(values.file, setFieldValue)) {
                            setSubmitting(false);
                            return;
                        }
                        setSubmitting(true);
                        uploadPatientActivityFiles(values, patientId)
                            .then(() => {
                                onHide(true); // true for refetching data from BE.
                            })
                            .catch(({ response }) => {
                                if (response?.status > 0) {
                                    let errorMsg = null;
                                    if (response?.data?.message) {
                                        errorMsg = `${response.status}: ${response.data.message}`;
                                    } else {
                                        errorMsg = `${response.status}: ${response.data}`;
                                    }
                                    setFieldValue('errorMsg', errorMsg);
                                }
                            })
                            .finally(() => {
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ isSubmitting, setFieldValue, handleSubmit, values }) => {
                        return (
                            <form onSubmit={handleSubmit} noValidate>
                                {notesCount > 0 && (
                                    <Row>
                                        <Col md={12}>
                                                <SelectField
                                                    label="Activity/Subject"
                                                    options={notes || []}
                                                    getOptionValue={R.prop('parentNoteId')}
                                                    getOptionLabel={R.prop('subject')}
                                                    fieldName="activity"
                                                    isClearable
                                                />
                                            <FormGroup>
                                                <FormLabel htmlFor="description">
                                                    Description {values.description?.length || 0}/2500
                                                </FormLabel>
                                                <textarea
                                                    id="description"
                                                    className="form-control"
                                                    placeholder="New Note"
                                                    rows="3"
                                                    maxLength="2500"
                                                    value={values.description}
                                                    onChange={(e) => setFieldValue('description', e.target.value)}
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <FormLabel htmlFor="file">
                                                    <b>File *</b>
                                                </FormLabel>
                                                <input
                                                    name="file"
                                                    id="file"
                                                    required
                                                    ref={fileInputRef}
                                                    type="file"
                                                    onChange={(event) => {
                                                        const file = event.currentTarget.files[0];
                                                        if (validateFile(file, setFieldValue)) {
                                                            setFieldValue('file', file);
                                                        }
                                                    }}
                                                />
                                            </FormGroup>
                                            {values.errorMsg && <div className="text-danger">{values.errorMsg}</div>}
                                            <FormGroup>
                                                <FormLabel> Maximum size: {uploadLimit}</FormLabel>
                                            </FormGroup>
                                            <div className="text-left text-danger">
                                                {values.showMaxSizeError && (
                                                    <div>
                                                        The selected file is too large.
                                                        {values.errorFileSize / 1000000} MB: max {uploadLimit}
                                                    </div>
                                                )}
                                                {values.showRequiredError && <div>Please select a file.</div>}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                                <div className="row">
                                    <div className="col-md-12 modal-footer">
                                        <div className="form-button-group">
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                disabled={isSubmitting}
                                                onClick={() => onHide(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                id="view-attachments-add-note-button"
                                                type="submit"
                                                variant="primary"
                                                disabled={isSubmitting}
                                            >
                                                Add Note
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Col>
        </Row>
    );
};

FileUploadForm.propTypes = propTypes;
FileUploadForm.defaultProps = defaultProps;

export default FileUploadForm;
